// Specimen carousel for the Fieldstone landers (/fieldstone-mac,
// /fieldstone-free). The markup ships as a scroll-snap strip, so swiping and
// keyboard scrolling already work with no JS at all. This module adds the
// three things that need it: prev/next arrows, the wrap-around clones that
// put the last specimen in the left gutter beside the first one, and the
// 6-second auto-rotate. All of it is built here rather than in the template
// so a JS failure leaves no dead buttons and no duplicate slides on the page.

const AUTO_ROTATE_MS = 6000;

// Neither a smooth programmatic scroll nor a swipe's momentum announces that
// it has finished, so "the strip stopped moving" is a quiet scroll event.
const SETTLE_MS = 160;

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

class SpecimenCarousel {
  /** The authored slides, in order. */
  private readonly slides: HTMLElement[];

  /** What the track holds once cloned: [copy of last, ...slides, copy of first]. */
  private readonly strip: HTMLElement[];

  /** Index into `slides` of whatever is centred (a clone counts as its twin). */
  private activeIndex = 0;

  private rotateTimer: number | null = null;
  private settleTimer: number | null = null;
  private viewportWidth = window.innerWidth;

  constructor(
    private readonly root: HTMLElement,
    private readonly track: HTMLElement,
    slides: HTMLElement[]
  ) {
    this.slides = slides;
    this.root.classList.add('is-enhanced');

    // Copying the two end slides is what makes the strip read as a loop: at
    // rest on the first specimen the last one is already peeking in from the
    // left. Scrolling past either end lands on a copy, and the settle handler
    // swaps in the real slide underneath it — invisible, because the two are
    // the same picture.
    const head = this.cloneSlide(this.slides[this.slides.length - 1]);
    const tail = this.cloneSlide(this.slides[0]);
    this.track.prepend(head);
    this.track.append(tail);
    this.strip = [head, ...this.slides, tail];

    // Same task as the prepend, so the browser never paints the copy centred.
    this.settleOn(1);

    this.buildArrows();
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Mobile browsers fire resize when the URL bar collapses mid-scroll;
    // re-centring on that would yank the strip out of the reader's swipe.
    window.addEventListener('resize', () => {
      if (window.innerWidth === this.viewportWidth) return;
      this.viewportWidth = window.innerWidth;
      this.settleOn(this.activeIndex + 1);
    });

    // Hovering or tabbing into the carousel means the visitor is reading it;
    // rotating out from under them is the one thing a carousel must not do.
    this.root.addEventListener('pointerenter', () => this.stop());
    this.root.addEventListener('pointerleave', () => this.start());
    this.root.addEventListener('focusin', () => this.stop());
    this.root.addEventListener('focusout', () => this.start());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.stop();
      else this.start();
    });

    this.start();
  }

  private cloneSlide(slide: HTMLElement): HTMLElement {
    const clone = slide.cloneNode(true) as HTMLElement;
    clone.setAttribute('aria-hidden', 'true');
    // Both copies sit in a gutter that is on screen from the first paint, so
    // deferring them would just leave the gutters blank.
    clone.querySelectorAll('img').forEach((img) => img.removeAttribute('loading'));
    return clone;
  }

  private buildArrows(): void {
    const make = (direction: 'prev' | 'next', glyph: string, label: string): void => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `fieldstone__carousel-arrow fieldstone__carousel-arrow--${direction}`;
      button.innerHTML = `<span aria-hidden="true">${glyph}</span>`;
      button.setAttribute('aria-label', label);
      button.addEventListener('click', () => {
        this.stop();
        this.step(direction === 'next' ? 1 : -1);
      });
      this.root.appendChild(button);
    };
    make('prev', '‹', 'Previous specimen');
    make('next', '›', 'Next specimen');
  }

  /** Move one slide either way. Off an end it scrolls onto a copy, never jumps. */
  private step(delta: number): void {
    const position = this.activeIndex + 1 + delta;
    this.scrollTo(position, prefersReducedMotion() ? 'instant' : 'smooth');
    this.setActive(position);
  }

  private readonly onScroll = (): void => {
    if (this.settleTimer !== null) window.clearTimeout(this.settleTimer);
    this.settleTimer = window.setTimeout(this.onSettled, SETTLE_MS);
  };

  private readonly onSettled = (): void => {
    this.settleTimer = null;
    const position = this.nearestPosition();
    if (position === 0) {
      this.settleOn(this.slides.length);
    } else if (position === this.strip.length - 1) {
      this.settleOn(1);
    } else {
      this.setActive(position);
    }
  };

  /** Park on a strip position with no animation — used to swap a copy for its twin. */
  private settleOn(position: number): void {
    this.scrollTo(position, 'instant');
    this.setActive(position);
  }

  private scrollTo(position: number, behavior: ScrollBehavior): void {
    const slide = this.strip[position];
    // scrollIntoView would also scroll the page vertically; nudging
    // scrollLeft keeps the movement inside the track.
    this.track.scrollTo({
      left: slide.offsetLeft - (this.track.clientWidth - slide.clientWidth) / 2,
      behavior,
    });
  }

  private setActive(position: number): void {
    this.activeIndex = (position - 1 + this.slides.length) % this.slides.length;
    this.strip.forEach((slide, i) => slide.classList.toggle('is-active', i === position));
  }

  /** Whichever strip slide is closest to the centre of the visible track. */
  private nearestPosition(): number {
    const centre = this.track.scrollLeft + this.track.clientWidth / 2;
    let nearest = 0;
    let shortest = Infinity;
    this.strip.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft + slide.clientWidth / 2 - centre);
      if (distance < shortest) {
        shortest = distance;
        nearest = i;
      }
    });
    return nearest;
  }

  private start(): void {
    if (this.rotateTimer !== null || prefersReducedMotion() || document.hidden) return;
    this.rotateTimer = window.setInterval(() => this.step(1), AUTO_ROTATE_MS);
  }

  private stop(): void {
    if (this.rotateTimer === null) return;
    window.clearInterval(this.rotateTimer);
    this.rotateTimer = null;
  }
}

function initFieldstoneCarousel(): void {
  document.querySelectorAll<HTMLElement>('[data-fieldstone-carousel]').forEach((root) => {
    const track = root.querySelector<HTMLElement>('[data-carousel-track]');
    if (!track) {
      throw new Error('[fieldstone-carousel] carousel is missing its [data-carousel-track] element.');
    }
    const slides = Array.from(track.querySelectorAll<HTMLElement>('.fieldstone__slide'));
    // One specimen is a picture, not a carousel. Leave it alone.
    if (slides.length < 2) return;
    new SpecimenCarousel(root, track, slides);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFieldstoneCarousel);
} else {
  initFieldstoneCarousel();
}
